import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { normalizeApiError, resolveApiErrorMessage } from '@accio3d/shared-ui/utils/apiErrors';

export interface ApiClientConfig {
  baseURL: string;
  apiPrefix?: string;
  /** Entra access token for the Bearer header. Pass `true` to bypass the MSAL cache. */
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  /** Current active organization id, attached as X-Organization-Id. */
  getOrgId: () => string | undefined;
  /** Unrecoverable 401 — typically clears the session and redirects to /auth. */
  onUnauthorized?: () => void;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  /** Replay token; without this the interceptor would re-attach the rejected one. */
  _bearer?: string;
}

/** Coalesces concurrent `getToken(true)` calls. */
function createTokenRefresher(getToken: ApiClientConfig['getToken']) {
  let inFlight: Promise<string | null> | null = null;

  return (): Promise<string | null> => {
    inFlight ??= (async () => {
      try {
        return await getToken(true);
      } catch {
        return null;
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  };
}

/**
 * Axios client that attaches Entra bearer + org headers, renews once on 401,
 * and calls `onUnauthorized` only if that fails.
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const { baseURL, apiPrefix = '/api/v1', getToken, getOrgId, onUnauthorized } = config;
  const instance = axios.create({
    baseURL: `${baseURL.replace(/\/$/, '')}${apiPrefix}`,
  });
  const refreshToken = createTokenRefresher(getToken);

  instance.interceptors.request.use(async (req: RetriableConfig) => {
    const headers =
      req.headers instanceof AxiosHeaders ? req.headers : new AxiosHeaders(req.headers);

    const orgId = getOrgId();
    if (orgId) headers.set('X-Organization-Id', orgId);

    try {
      const token = req._bearer ?? (await getToken());
      if (token) headers.set('Authorization', `Bearer ${token}`);
    } catch {
      // Proceed without a header; the server 401 is handled below.
    }

    req.headers = headers;
    return req;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      normalizeApiError(error);

      const original = error.config as RetriableConfig | undefined;
      if (error.response?.status !== 401) return Promise.reject(error);

      if (!original || original._retried) {
        onUnauthorized?.();
        return Promise.reject(error);
      }
      original._retried = true;

      const token = await refreshToken();
      if (!token) {
        onUnauthorized?.();
        return Promise.reject(error);
      }
      original._bearer = token;

      return instance.request(original);
    },
  );

  return instance;
}

export type OpenStream = (
  path: string,
  body: unknown,
  signal?: AbortSignal,
) => Promise<ReadableStream<Uint8Array>>;

export function createSseClient(config: ApiClientConfig): OpenStream {
  const { baseURL, apiPrefix = '/api/v1', getToken, getOrgId, onUnauthorized } = config;
  const root = `${baseURL.replace(/\/$/, '')}${apiPrefix}`;
  const refreshToken = createTokenRefresher(getToken);

  return async function openStream(path, body, signal) {
    const send = async (token: string | null) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };

      const orgId = getOrgId();
      if (orgId) headers['X-Organization-Id'] = orgId;
      if (token) headers.Authorization = `Bearer ${token}`;

      return fetch(`${root}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal,
      });
    };

    let token: string | null = null;
    try {
      token = await getToken();
    } catch {
      // Proceed without a header; a 401 is handled below.
    }

    let response = await send(token);

    if (response.status === 401) {
      const renewed = await refreshToken();
      if (renewed) response = await send(renewed);
    }

    if (!response.ok) {
      if (response.status === 401) onUnauthorized?.();
      throw new Error(await streamErrorMessage(response));
    }
    if (!response.body) throw new Error('The assistant response had no body.');

    return response.body;
  };
}

async function streamErrorMessage(response: Response): Promise<string> {
  const fallback = `The assistant request failed (${response.status}).`;
  try {
    const message = resolveApiErrorMessage(response.status, await response.json());
    return message ?? fallback;
  } catch {
    return fallback;
  }
}
